import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Box } from "@mui/system";
import toast from "../../../../helpers/toast";
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Typography,
  Table,
  TableContainer,
  TableBody,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import {
  StyledSelect,
  StyledTextField,
} from "../../../customComponents/styled/styledInputs";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../customComponents/styled/styledTable";
import DeleteIcon from "@mui/icons-material/Delete";
import { StyledLoadingButton } from "../../../customComponents/styled/styledButtons";
import CustomWarningDialog from "../../../customComponents/CustomWarningDialog";

import { useGetTemplatesQuery } from "./sectionsApiSlice";
import {
  useAddTemplateToSubSectionMutation,
  useRemoveTemplateMutation,
  useUpdateSubSectionMutation,
  useGetSectionQuery,
  useGetSubSectionQuery,
  useGetSectionsPaginatedQuery,
} from "./sectionsApiSlice";
import { useState } from "react";
import PreviewIcon from "@mui/icons-material/Preview";
import TemplatePreviewModal from "../../Templates/TemplatePreviewModal";

const subSectionFormValidation = yup.object({
  name: yup.string().required("Required"),
  reportType: yup.string().required("Required"),
  section_id: yup.string().required("Required"),
  // reportTemplate: yup.string().required("Required"),
});

function EditSubSectionForm({ item, setFocusedSubSection }) {
  const { data: templates, isLoading: templatesLoading } =
    useGetTemplatesQuery("all");
  const { data: subsections, isLoading: subsectionsLoading } =
    useGetSubSectionQuery("all");
  const [addTemplateToSubSection, { isLoading: isAddingTemplateToSubSection }] =
    useAddTemplateToSubSectionMutation();
  const [updateSubSection, { isLoading: isUpdatingSubSection }] =
    useUpdateSubSectionMutation();
  const [removeTemplate] = useRemoveTemplateMutation();
  const { data: sections, isLoading: sectionsLoading } =
    useGetSectionQuery("all");

  const currentItem = subsections?.find(
    (subsection) => subsection.id === item.id
  );

  const [deleteTemplate, setDeleteTemplate] = useState(null);
  const [openWarning, setOpenWarning] = useState(false);
  const [newTemplateIds, setNewTemplateIds] = useState([]);
  const [openImageViewer, setOpenImageViewer] = React.useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [sectionSearch, setSectionSearch] = useState("");
  const { data: sectionData } = useGetSectionsPaginatedQuery({ page: 1, per_page: 10, q: sectionSearch });
  const sectionOptions = sectionData?.sections ?? [];

  const handleTemplateDelete = async (template) => {
    try {
      const body = {
        subsection_id: item.id,
        template_id: template.id,
      };

      const response = await removeTemplate(body).unwrap();
      if (response) {
        toast.success("Template removed successfully");
        //  const test = sections?.find((section) => section.subsections.find((subsection) => subsection.id === item.id));
      }
    } catch (error) {
      if (error.status === 404) {
        toast.success("Template removed successfully");
      }
    }
  };

  const subSectionForm = useFormik({
    initialValues: {
      name: item.name,
      reportType: item.report_type,
      reportTemplate: item.templates.name,
      section_id:
        sections?.find((section) =>
          section.subsections.find((subsection) => subsection.id === item.id)
        )?.id || "",
    },
    enableReinitialize: true,
    validationSchema: subSectionFormValidation,
    onSubmit: async (values) => {
      try {
        const update = {
          id: item.id || "",
          name: values.name || "",
          report_type: values.reportType || "",
          section_id: values.section_id || "",
          head_id: item.head?.id || "",
          head_title:
            sections?.find((section) => section.id === values.section_id)
              ?.head_title || "",
        };

        const updateResponse = await updateSubSection(update).unwrap();
        if (updateResponse) {
          toast.success(`${item.name} sub-section updated successfully`);

          newTemplateIds.forEach(async (templateId) => {
            await addTemplateToSubSection({
              subsection_id: item?.id,
              template_id: templateId,
            }).unwrap();
            toast.success("Template updated successfully");
            setNewTemplateIds([]); //reset template table
          });
        } else {
          toast.error("Template was not added, please try again");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  return (
    <>
      <CustomWarningDialog
        open={openWarning}
        setOpen={setOpenWarning}
        item={deleteTemplate}
        action={handleTemplateDelete}
        message="Are you sure you want to remove this template?"
      />

      <TemplatePreviewModal
        open={openImageViewer}
        onClose={() => {
          setOpenImageViewer(false);
          setPreviewTemplate(null);
        }}
        template={previewTemplate}
      />

      <Box className="paperHeader">
        <Typography variant="formHeading">Edit Sub-Section</Typography>
      </Box>
      <Box className="paperBody">
        <form onSubmit={subSectionForm.handleSubmit}>
          <Grid container columnSpacing={1} rowSpacing={2}>
            <Grid item xs={12}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">
                  Sub-Section Name
                </Typography>
                <StyledTextField
                  autoFocus
                  variant="outlined"
                  id="name"
                  name="name"
                  value={subSectionForm.values.name}
                  onChange={subSectionForm.handleChange}
                  error={
                    subSectionForm.touched.name &&
                    Boolean(subSectionForm.errors.name)
                  }
                />
                <FormHelperText variant="error">
                  {subSectionForm.touched.name && subSectionForm.errors.name}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">Section</Typography>
                <Autocomplete
                  options={sectionOptions}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={sectionOptions.find((s) => s.id === subSectionForm.values.section_id) || null}
                  onChange={(_, value) =>
                    subSectionForm.setFieldValue("section_id", value?.id || "")
                  }
                  onInputChange={(_, value, reason) => {
                    if (reason === "input") setSectionSearch(value);
                  }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder="Search section..."
                      error={
                        subSectionForm.touched.section_id &&
                        Boolean(subSectionForm.errors.section_id)
                      }
                    />
                  )}
                />
                <FormHelperText variant="error">
                  {subSectionForm.touched.section_id &&
                    subSectionForm.errors.section_id}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">Report Type</Typography>
                <StyledSelect
                  id="reportType"
                  name="reportType"
                  value={subSectionForm.values.reportType}
                  onChange={subSectionForm.handleChange}
                  error={
                    subSectionForm.touched.reportType &&
                    Boolean(subSectionForm.errors.reportType)
                  }
                >
                  <MenuItem value="score">Score Based</MenuItem>
                  <MenuItem value="skill">Comment/Skill Based</MenuItem>
                </StyledSelect>
                <FormHelperText variant="error">
                  {subSectionForm.touched.reportType &&
                    subSectionForm.errors.reportType}
                </FormHelperText>
              </FormControl>
            </Grid>
            {/* <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">
                  Report Template
                </Typography>
                <StyledSelect
                  id="reportTemplate"
                  name="reportTemplate"
                  value={subSectionForm.values.reportTemplate}
                  onChange={subSectionForm.handleChange}
                  error={
                    subSectionForm.touched.reportTemplate &&
                    Boolean(subSectionForm.errors.reportTemplate)
                  }
                >
                  <MenuItem value="">
                    <em>Select a Template</em>
                  </MenuItem>
                  {templates?.map((template) => (
                    <MenuItem value={template.id} key={template.id}>
                      {" "}
                      {template.name}
                    </MenuItem>
                  ))}
                </StyledSelect>
                <FormHelperText variant="error">
                  {subSectionForm.touched.reportTemplate &&
                    subSectionForm.errors.reportTemplate}
                </FormHelperText>
              </FormControl>
            </Grid> */}

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">Templates</Typography>
                <StyledSelect
                  id="reportTemplate"
                  name="reportTemplate"
                  displayEmpty
                  // value={subSectionForm.values.reportTemplate}
                  // onChange={subSectionForm.handleChange}
                  onChange={(e) => {
                    const newTemplateId = e.target.value;
                    //add template id to template array
                    if (!newTemplateIds?.includes(newTemplateId)) {
                      setNewTemplateIds((prev) => {
                        return [...prev, newTemplateId];
                      });
                    } else {
                      toast.info("Template is already selected");
                    }
                  }}
                  error={
                    subSectionForm.touched.reportTemplate &&
                    Boolean(subSectionForm.errors.reportTemplate)
                  }
                >
                  <MenuItem value="">
                    <em>Select a Template</em>
                  </MenuItem>
                  {templates?.map((template) => (
                    <MenuItem value={template.id} key={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </StyledSelect>
                <FormHelperText variant="error">
                  {subSectionForm.touched.reportTemplate &&
                    subSectionForm.errors.reportTemplate}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
          {/* sub-section templates table */}
          <Typography variant="formHeading" sx={{ padding: "25px 0 5px 15px" }}>
            {item.name} templates
          </Typography>

          <TableContainer sx={{ pb: 4 }}>
            <Table sx={{ width: "100%", boxShadow: "none" }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Section </StyledTableCell>
                  <StyledTableCell align="right">Preview </StyledTableCell>
                  <StyledTableCell align="right">delete</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentItem?.templates?.map((template) => (
                  <StyledTableRow key={template.id}>
                    <StyledTableCell component="th" scope="section">
                      {template.name}
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      scope="section"
                      align="right"
                    >
                      <PreviewIcon
                        className="tableCellLink"
                        onClick={() => {
                          setPreviewTemplate(template);
                          setOpenImageViewer(true);
                        }}
                      />
                    </StyledTableCell>

                    <StyledTableCell align="right">
                      <DeleteIcon
                        className="tableCellLink"
                        onClick={() => {
                          setDeleteTemplate(template);
                          setOpenWarning(true);
                        }}
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
                {/* The newly selected ones */}
                {newTemplateIds?.map((templateId) => (
                  <StyledTableRow key={templateId}>
                    <StyledTableCell component="th" scope="section">
                      {
                        templates.find((template) => template.id === templateId)
                          ?.name
                      }
                    </StyledTableCell>
                    <StyledTableCell
                      component="th"
                      scope="section"
                      align="right"
                    >
                      <PreviewIcon
                        className="tableCellLink"
                        onClick={() => {
                          setPreviewTemplate(
                            templates.find(
                              (template) => template.id === templateId
                            )
                          );
                          setOpenImageViewer(true);
                        }}
                      />
                    </StyledTableCell>

                    <StyledTableCell align="right">
                      <DeleteIcon
                        className="tableCellLink"
                        onClick={() => {
                          const filteredTemplates = newTemplateIds?.filter(
                            (template) => template !== templateId
                          );
                          setNewTemplateIds(filteredTemplates);
                        }}
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" gap={1} justifyContent="right">
            <StyledLoadingButton
              variant="contained"
              onClick={() => setFocusedSubSection(null)}
            >
              Cancel
            </StyledLoadingButton>
            <StyledLoadingButton
              type="submit"
              loading={isAddingTemplateToSubSection || isUpdatingSubSection}
            >
              Update
            </StyledLoadingButton>
          </Stack>
        </form>
      </Box>
    </>
  );
}

export default EditSubSectionForm;
